import type { PersistentStore } from "./store";

export interface DebouncedSaverOptions {
  readonly delayMs: number;
}

export class DebouncedSaver<T> {
  private readonly delayMs: number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: { value: T } | null = null;

  constructor(
    private readonly store: PersistentStore<T>,
    options: DebouncedSaverOptions,
  ) {
    if (!Number.isFinite(options.delayMs) || options.delayMs < 0) {
      throw new RangeError(
        `DebouncedSaver delay must be a non-negative finite number, got ${String(options.delayMs)}`,
      );
    }
    this.delayMs = options.delayMs;
  }

  schedule(value: T): void {
    this.pending = { value };
    this.timer ??= setTimeout(() => {
      this.timer = null;
      this.flush();
    }, this.delayMs);
  }

  flush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const pending = this.pending;
    if (pending === null) return;
    this.pending = null;
    this.store.save(pending.value);
  }

  cancel(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.pending = null;
  }

  get hasPending(): boolean {
    return this.pending !== null;
  }
}
